import { User } from '../types';
import { isNativeApp, phpAuthHeaders, REMOTE_API_BASE } from '../apiEnv';

export type OnlineRole = 'PLAYER' | 'AUDIENCE';

export interface OnlineRoomSummary {
  id: string;
  name: string;
  userCount: number;
  hasPassword: boolean;
  isSaved?: boolean;
}

export interface OnlineChatMsg {
  id: number;
  text: string;
  sender: string;
  time: string;
}

export interface OnlineRoomUser {
  id: string;
  sessionId?: string;
  name: string;
  email: string;
  role: OnlineRole;
  isHost?: boolean;
}

export interface OnlineBattleEvent {
  id: string;
  winnerId: string | null;
  loserId?: string | null;
  isTie: boolean;
  time: number;
  resolvedAt: string;
  votes: Record<string, string>;
}

export interface OnlineSignalMsg {
  id: string;
  from: string;
  type: 'offer' | 'answer' | 'candidate' | 'bye' | string;
  payload: any;
}

interface OnlineSyncResponse {
  success?: boolean;
  onlineUsers?: number;
  activeRooms?: OnlineRoomSummary[];
  lobbyChat?: OnlineChatMsg[];
  error?: string;
}

interface OnlineRoomResponse {
  success?: boolean;
  roomUsers?: OnlineRoomUser[];
  roomChat?: OnlineChatMsg[];
  battleActive?: boolean;
  battleVotes?: Record<string, string>;
  battleEvent?: OnlineBattleEvent | null;
  roomId?: string;
  isSaved?: boolean;
  signals?: OnlineSignalMsg[];
  error?: string;
}

const reportDebugEvent = (hypothesisId: 'A' | 'B' | 'C' | 'D', location: string, msg: string, data: Record<string, unknown> = {}) => {
  void hypothesisId; void location; void msg; void data;
};

const ACTION_PATHS: Record<string, string> = {
  online_sync: 'online/sync',
  online_send_lobby_chat: 'online/lobby-chat',
  online_create_room: 'online/create-room',
  online_join_room: 'online/join-room',
  online_room_state: 'online/room-state',
  online_change_role: 'online/change-role',
  online_send_room_chat: 'online/room-chat',
  online_leave_room: 'online/leave-room',
  online_delete_room: 'online/delete-room',
  online_battle_result: 'online/battle-result',
  online_start_battle: 'online/start-battle',
  online_battle_vote: 'online/battle-vote',
  online_reset_battle: 'online/reset-battle',
  online_signal: 'online/signal',
  online_save_room: 'online/save-room',
};

const buildOnlineApiUrl = (action: string, query?: Record<string, string>) => {
  const path = ACTION_PATHS[action];
  // 原生 App 的页面来自 Capacitor 内置 localhost，相对地址会打到 App 自身，
  // 必须使用线上绝对地址；网页部署继续用相对地址以兼容 /myapp 子路径托管。
  const nativeApp = isNativeApp();
  const href = typeof window !== 'undefined' ? window.location.href : 'http://localhost/';
  const url = nativeApp
    ? new URL(`${REMOTE_API_BASE}/api.php${path ? `/${path}` : ''}`)
    : path
      ? new URL(`./api.php/${path}`, href)
      : new URL('./api.php', href);

  if (!path) {
    url.searchParams.set('action', action);
  }

  if (query) {
    Object.entries(query).forEach(([key, value]) => {
      url.searchParams.set(key, value);
    });
  }

  return url.toString();
};

const readJson = async <T>(response: Response): Promise<T> => {
  const rawText = await response.text();
  let payload: any = null;

  try {
    payload = rawText ? JSON.parse(rawText) : {};
  } catch {
    const preview = rawText.slice(0, 120).replace(/\s+/g, ' ').trim();
    throw new Error(preview ? `Server did not return JSON: ${preview}` : 'Server did not return JSON');
  }

  if (!response.ok) {
    throw new Error(payload?.error || `HTTP ${response.status}`);
  }
  return payload as T;
};

// 弱网/QUIC 抖动时请求可能长时间不返回：加超时，并在"连接层失败"时重试一次，
// 避免按钮一直卡在 pending 状态、用户以为点了没反应。
const ONLINE_REQUEST_TIMEOUT_MS = 7000;

// 连续失败计数：通道被卡死时（例如 QUIC 挂起）主动重载 WebView 自愈，避免一直点不动
const ONLINE_FAILURE_COUNT_KEY = 'kendama-online-failure-count';
const ONLINE_RELOAD_AT_KEY = 'kendama-online-last-reload';
let consecutiveFailures = 0;

const noteOnlineSuccess = () => {
  consecutiveFailures = 0;
  try { window.sessionStorage.removeItem(ONLINE_FAILURE_COUNT_KEY); } catch { /* 忽略 */ }
};

const noteOnlineFailure = () => {
  consecutiveFailures += 1;
  try { window.sessionStorage.setItem(ONLINE_FAILURE_COUNT_KEY, String(consecutiveFailures)); } catch { /* 忽略 */ }
  if (consecutiveFailures < 4) return;
  try {
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
    const lastReload = Number(window.sessionStorage.getItem(ONLINE_RELOAD_AT_KEY) || 0);
    if (Date.now() - lastReload < 60000) return;
    window.sessionStorage.setItem(ONLINE_RELOAD_AT_KEY, String(Date.now()));
  } catch {
    // 忽略
  }
  consecutiveFailures = 0;
  window.location.reload();
};

// 联机请求失败时把「动作 / 错误名 / 错误信息」记到 localStorage，方便在模拟器里排查
const ONLINE_DEBUG_KEY = 'kendama-online-debug';

const recordOnlineFailure = (action: string, attempt: number, error: unknown) => {
  try {
    const list = JSON.parse(window.localStorage.getItem(ONLINE_DEBUG_KEY) || '[]');
    list.push({
      ts: Date.now(),
      action,
      attempt,
      name: (error as Error)?.name || 'Error',
      message: (error as Error)?.message || String(error),
    });
    window.localStorage.setItem(ONLINE_DEBUG_KEY, JSON.stringify(list.slice(-30)));
  } catch {
    // 忽略诊断写入失败
  }
};

const fetchOnlineWithTimeout = async (
  url: string,
  init: RequestInit,
  retryOnNetworkError: boolean,
  action: string
): Promise<Response> => {
  const run = async (attemptIndex: number) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), ONLINE_REQUEST_TIMEOUT_MS);
    try {
      const response = await fetch(url, { ...init, signal: controller.signal });
      // 能拿到响应就说明通道是通的（哪怕是 4xx）
      noteOnlineSuccess();
      return response;
    } catch (error) {
      recordOnlineFailure(action, attemptIndex, error);
      throw error;
    } finally {
      clearTimeout(timer);
    }
  };

  try {
    return await run(1);
  } catch (error) {
    const name = (error as Error)?.name;
    const isNetworkLevel = name === 'AbortError' || error instanceof TypeError;
    if (retryOnNetworkError && isNetworkLevel) {
      try {
        return await run(2);
      } catch (retryError) {
        noteOnlineFailure();
        throw retryError;
      }
    }
    noteOnlineFailure();
    throw error;
  }
};

const postOnline = async <T>(
  action: string,
  body: Record<string, unknown>,
  options?: { retryOnNetworkError?: boolean }
): Promise<T> => {
  const url = buildOnlineApiUrl(action);
  // #region debug-point B:post-online-request
  reportDebugEvent('B', 'onlineHttpApi.ts:postOnline', 'posting online request', {
    action,
    url,
    hasSessionId: Boolean(body.sessionId),
    hasUser: Boolean(body.user),
    roomId: typeof body.roomId === 'string' ? body.roomId : null
  });
  // #endregion
  const response = await fetchOnlineWithTimeout(
    url,
    {
      method: 'POST',
      // 原生 App 跨域请求不带 Cookie，改由 X-Kendama-Auth 头做鉴权
      credentials: isNativeApp() ? 'omit' : 'include',
      headers: phpAuthHeaders({
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify(body),
    },
    options?.retryOnNetworkError === true,
    action
  );

  // #region debug-point B:post-online-response
  reportDebugEvent('B', 'onlineHttpApi.ts:postOnline', 'online request returned response', {
    action,
    url,
    status: response.status,
    ok: response.ok
  });
  // #endregion

  return readJson<T>(response);
};

const getOnline = async <T>(action: string, query?: Record<string, string>): Promise<T> => {
  // GET 是幂等的：网络层失败时重试一次，减少 "Load failed" 直接弹给用户
  const response = await fetchOnlineWithTimeout(
    buildOnlineApiUrl(action, query),
    {
      method: 'GET',
      credentials: isNativeApp() ? 'omit' : 'include',
      headers: phpAuthHeaders(),
    },
    true,
    action
  );

  return readJson<T>(response);
};

const resolveOnlineIdentity = (user: User) => ({
  name: user.displayName,
  email: user.email,
});

export const createOnlineSessionId = () =>
  `online-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

export const syncOnlineLobby = async (sessionId: string, user: User) => {
  return postOnline<OnlineSyncResponse>('online_sync', {
    sessionId,
    user: resolveOnlineIdentity(user),
  });
};

export const sendOnlineLobbyChat = async (sessionId: string, user: User, message: string) => {
  return postOnline<OnlineSyncResponse>('online_send_lobby_chat', {
    sessionId,
    message,
    sender: resolveOnlineIdentity(user).name,
    user: resolveOnlineIdentity(user),
  });
};

export const createOnlineRoom = async (
  sessionId: string,
  user: User,
  roomId: string,
  roomName: string,
  password?: string
) => {
  return postOnline<OnlineRoomResponse>('online_create_room', {
    sessionId,
    roomId,
    roomName,
    password,
    user: resolveOnlineIdentity(user),
  }, { retryOnNetworkError: true });
};

export const joinOnlineRoom = async (
  sessionId: string,
  user: User,
  roomId: string,
  password?: string
) => {
  return postOnline<OnlineRoomResponse>('online_join_room', {
    sessionId,
    roomId,
    password,
    user: resolveOnlineIdentity(user),
  }, { retryOnNetworkError: true });
};

export const leaveOnlineRoom = async (sessionId: string, roomId: string) => {
  return postOnline<OnlineRoomResponse>('online_leave_room', {
    sessionId,
    roomId,
  }, { retryOnNetworkError: true });
};

export const deleteOnlineRoom = async (
  sessionId: string,
  user: User,
  roomId: string
) => {
  return postOnline<OnlineRoomResponse>('online_delete_room', {
    sessionId,
    roomId,
    user: resolveOnlineIdentity(user),
  }, { retryOnNetworkError: true });
};

export const getOnlineRoomState = async (sessionId: string, user: User, roomId: string) => {
  return getOnline<OnlineRoomResponse>('online_room_state', {
    sessionId,
    roomId,
    email: user.email,
  });
};

export const changeOnlineRole = async (
  sessionId: string,
  roomId: string,
  role: OnlineRole
) => {
  return postOnline<OnlineRoomResponse>('online_change_role', {
    sessionId,
    roomId,
    role,
  }, { retryOnNetworkError: true });
};

export const sendOnlineRoomChat = async (
  sessionId: string,
  user: User,
  roomId: string,
  message: string,
  sender?: string
) => {
  return postOnline<OnlineRoomResponse>('online_send_room_chat', {
    sessionId,
    roomId,
    message,
    sender: sender || resolveOnlineIdentity(user).name,
    user: resolveOnlineIdentity(user),
  });
};

export const sendOnlineBattleResult = async (
  sessionId: string,
  roomId: string,
  winnerId: string,
  loserId: string | undefined,
  judgeId: string,
  time: number
) => {
  return postOnline<OnlineRoomResponse>('online_battle_result', {
    sessionId,
    roomId,
    winnerId,
    loserId,
    judgeId,
    time,
  });
};

export const startOnlineBattle = async (sessionId: string, roomId: string) => {
  return postOnline<OnlineRoomResponse>('online_start_battle', {
    sessionId,
    roomId,
  }, { retryOnNetworkError: true });
};

export const voteOnlineBattle = async (
  sessionId: string,
  roomId: string,
  winnerEmail: string
) => {
  return postOnline<OnlineRoomResponse>('online_battle_vote', {
    sessionId,
    roomId,
    winnerEmail,
  }, { retryOnNetworkError: true });
};

export const resetOnlineBattle = async (sessionId: string, roomId: string) => {
  return postOnline<OnlineRoomResponse>('online_reset_battle', {
    sessionId,
    roomId,
  }, { retryOnNetworkError: true });
};

// WebRTC 信令中转：offer / answer / candidate / bye
export const sendOnlineSignal = async (
  sessionId: string,
  roomId: string,
  type: 'offer' | 'answer' | 'candidate' | 'bye',
  payload: unknown,
  to?: string
) => {
  return postOnline<{ success?: boolean }>('online_signal', {
    sessionId,
    roomId,
    type,
    payload,
    to: to || '',
  }, { retryOnNetworkError: true });
};

export const saveOnlineRoom = async (
  sessionId: string,
  user: User,
  roomId: string
) => {
  return postOnline<OnlineRoomResponse>('online_save_room', {
    sessionId,
    roomId,
    user: resolveOnlineIdentity(user),
  });
};
